import { HelperText01, Label01 } from 'module/ui/components/styles';
import {
  faBookSparkles,
  faGlobe,
  faLink,
  faPaperclip,
} from '@fortawesome/pro-regular-svg-icons';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { Icon } from '@rocketlane/ui';
import { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { AddKnowledgeTriggerType } from '../add-knowledge-trigger';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
`;
const Option = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  border: 1px solid var(--divider-default);
  border-radius: var(--border-radius-small);
  padding: var(--spacing-05);
  cursor: pointer;
  ${({ $selected }) =>
    $selected &&
    css`
      border-color: var(--blue-50);
    `};
`;
const OptionLabel = styled.div`
  ${Label01};
`;
const OptionIcon = styled(Icon)`
  color: var(--icon-secondary);
  flex-shrink: 0;
`;
const SelectedIndicator = styled(OptionIcon).attrs({
  icon: faCheckCircle,
  size: '1x',
})`
  color: var(--blue-60);
`;
const OptionDescription = styled.div`
  ${HelperText01};
  color: var(--black-70);
`;
const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-02);
  flex: 1;
`;

interface Props {
  value?: AddKnowledgeTriggerType;
  onChange?: (value: AddKnowledgeTriggerType) => void;
}

export const ContentTypeSelector: FC<Props> = ({ value, onChange }) => {
  const options = useMemo(() => {
    return [
      {
        icon: faBookSparkles,
        label: (
          <FormattedMessage id="knowledge_base.folder.create.content_type.ai_pages.label" />
        ),
        description: (
          <FormattedMessage id="knowledge_base.folder.create.content_type.ai_pages.description" />
        ),
        value: AddKnowledgeTriggerType.AiPages,
      },
      {
        icon: faLink,
        label: (
          <FormattedMessage id="knowledge_base.folder.create.content_type.api_doc.label" />
        ),
        description: (
          <FormattedMessage id="knowledge_base.folder.create.content_type.api_doc.description" />
        ),
        value: AddKnowledgeTriggerType.ApiDoc,
      },
      {
        icon: faGlobe,
        label: (
          <FormattedMessage id="knowledge_base.folder.create.content_type.import_website.label" />
        ),
        description: (
          <FormattedMessage id="knowledge_base.folder.create.content_type.import_website.description" />
        ),
        value: AddKnowledgeTriggerType.ImportWebsite,
      },
      {
        icon: faPaperclip,
        label: (
          <FormattedMessage id="knowledge_base.folder.create.content_type.files.label" />
        ),
        description: (
          <FormattedMessage id="knowledge_base.folder.create.content_type.files.description" />
        ),
        value: AddKnowledgeTriggerType.Files,
      },
    ];
  }, []);

  return (
    <Wrapper>
      {options.map((option) => (
        <Option
          key={option.value}
          $selected={value === option.value}
          onClick={() => onChange?.(option.value)}
        >
          <OptionIcon icon={option.icon} size="1x" />
          <ContentWrapper>
            <OptionLabel>{option.label}</OptionLabel>
            <OptionDescription>{option.description}</OptionDescription>
          </ContentWrapper>
          {value === option.value && (
            <SelectedIndicator icon={faCheckCircle} size="1x" />
          )}
        </Option>
      ))}
    </Wrapper>
  );
};
